import { useCallback, useEffect, useMemo, useRef, useState, type AnimationEvent } from 'react';

export type SlidePhase = '' | 'in' | 'out';

export interface Slide {
  open: boolean;
  /** '' at rest; 'in' or 'out' only while the move is running. */
  phase: SlidePhase;
  setOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
  /** Put on the sliding element — clears the phase when the move really ends. */
  onAnimationEnd: (e: AnimationEvent<Element>) => void;
}

// ONE lifecycle for every drawer in the app, so the sheet and the rail cannot
// drift apart in how they open and close. Two rules are baked in, both learned
// the hard way on this branch:
//
//  1. THE OPEN FLAG AND ITS PHASE ARE ONE PIECE OF STATE. Held apart — the flag
//     in state, the class assigned by an effect afterwards — React commits the
//     flag first and the panel paints at its open position for a frame before
//     the class arrives and snaps it back to start. Measured on the rail: 49ms
//     open, 66ms back at the top, then the slide. Set together, the class is
//     there in the very first painted frame.
//
//  2. THE PHASE ENDS WITH THE ANIMATION, NOT WITH A STOPWATCH. A fixed timer
//     assumes the move began the instant it was asked for; it does not. Setting
//     state, re-rendering and applying the class took 54ms on the sheet, so a
//     260ms timer tore it out with 140px still on screen. `animationend` fires
//     when the move has actually finished, whenever it started.
//
// The timeout survives only as a backstop: if the animation never runs — a
// throttled frame, a paint-starved tab — the phase must still clear, or the
// panel is stuck wearing a one-shot class for ever.
// Read at the moment of the move rather than subscribed to: the preference does
// not change mid-gesture, and a listener here would be state in every drawer.
function prefersReduced(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useSlide(initial = false, backstopMs = 700): Slide {
  const [state, setState] = useState<{ open: boolean; phase: SlidePhase }>({
    open: initial,
    phase: '',
  });
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const setOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setState((cur) => {
      const open = typeof next === 'function' ? next(cur.open) : next;
      if (open === cur.open) return cur;
      // NO PHASE WHERE THERE IS NO ANIMATION. The stylesheet switches every
      // drawer keyframe off under prefers-reduced-motion, and `animationend` is
      // what clears the phase — so a reduced-motion viewer waited out the 700ms
      // backstop on every dismiss: a modal frozen mid-close, and a rail scrim
      // still swallowing taps for the same window. Declining motion should make
      // the app quicker, not lock it.
      if (open === false && prefersReduced()) return { open, phase: '' };
      return { open, phase: open ? 'in' : 'out' };
    });
  }, []);

  useEffect(() => {
    if (!state.phase) return;
    timer.current = window.setTimeout(
      () => setState((c) => ({ ...c, phase: '' })),
      backstopMs,
    );
    return () => window.clearTimeout(timer.current);
  }, [state.phase, state.open, backstopMs]);

  const onAnimationEnd = useCallback((e: AnimationEvent<Element>) => {
    // A child's animation is not this panel's.
    if (e.target !== e.currentTarget) return;
    setState((c) => ({ ...c, phase: '' }));
  }, []);

  // Memoised because consumers put this object in effect dependency arrays. A
  // fresh literal each render made MasterDetailShell's Escape listener tear
  // down and re-register on every single render of an open modal.
  return useMemo(
    () => ({ open: state.open, phase: state.phase, setOpen, onAnimationEnd }),
    [state.open, state.phase, setOpen, onAnimationEnd],
  );
}
